"use client";

import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { extractImageCropFromTag } from "@faire/web/common/images/cropUtils/extractImageCropFromTag";
import {
  optimizeImageUrl,
  optimizeImageUrlFillSquare,
} from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import * as React from "react";

import { useResponsiveValue } from "../hooks/useResponsiveValue";

import {
  Circle,
  Image,
  INNER_DIAMETER_LARGE,
  OUTER_DIAMETER_LARGE,
  StyledLabel,
  SwatchWrapper,
  VisuallyHiddenRadioInput,
} from "./styles";
import { Swatch as SwatchProps } from "./types";

/**
 * @trackcomponent
 */
export const Swatch = ({
  "aria-label": ariaLabel,
  "data-test-id": dataTestId,
  color,
  image,
  isAvailable = true,
  isChecked = false,
  isDisabled = false,
  name,
  onChange,
  swatchSize = {
    mobileAndAbove: INNER_DIAMETER_LARGE,
    tabletAndAbove: INNER_DIAMETER_LARGE,
    desktopAndAbove: INNER_DIAMETER_LARGE,
  },
  swatchWrapperSize = {
    mobileAndAbove: OUTER_DIAMETER_LARGE,
    tabletAndAbove: OUTER_DIAMETER_LARGE,
    desktopAndAbove: OUTER_DIAMETER_LARGE,
  },
  ...rest
}: SwatchProps.IProps) => {
  const { strictLocalize } = useStrictLocalization();
  const labelId = useUniqueId("swatch-label-");

  const [imageError, setImageError] = React.useState(false);
  const isSSR = useIsSSR();

  const responsiveSwatchSize = useResponsiveValue(swatchSize);
  const responsiveSwatchWrapperSize = useResponsiveValue(swatchWrapperSize);

  const onImageError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const unavailable: string = strictLocalize({
    id: "Ec6S71",
    defaultMessage: "Unavailable",
    description: {
      text: "alt text for screen reader, to indicate the swatch option is unavailable",
    },
  });

  const accessibleLabel = isAvailable
    ? ariaLabel
    : `${unavailable} ${ariaLabel}`;

  return (
    <SwatchWrapper>
      <VisuallyHiddenRadioInput
        aria-label={accessibleLabel}
        checked={isChecked}
        data-test-id={dataTestId}
        disabled={isDisabled}
        id={labelId}
        name={name}
        onChange={onChange}
        readOnly={onChange === undefined}
        type="radio"
        {...rest}
      />
      <StyledLabel
        $available={isAvailable}
        $checked={isChecked}
        $disabled={isDisabled}
        $outerSize={responsiveSwatchWrapperSize}
        htmlFor={labelId}
      >
        {image && !imageError ? (
          <Image
            alt="" // This empty alt is on purpose, aria-label provides the value
            onError={onImageError}
            src={optimizeSwatchImage(image, isSSR)}
            $innerSize={responsiveSwatchSize}
          />
        ) : (
          <Circle color={color} $innerSize={responsiveSwatchSize} />
        )}
      </StyledLabel>
    </SwatchWrapper>
  );
};

export const optimizeSwatchImage = (image: Partial<IImage>, isSSR: boolean) => {
  if (extractImageCropFromTag(image.tags)) {
    return optimizeImageUrl(image, isSSR, {
      width: INNER_DIAMETER_LARGE,
      height: INNER_DIAMETER_LARGE,
    });
  }
  return optimizeImageUrlFillSquare(image, isSSR, INNER_DIAMETER_LARGE);
};
